import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateOnPremVideoAssetSubtitleRequest } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremVideoAssetSubtitleRequest";
import { UpdateOnPremVideoAssetSubtitleResponse } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremVideoAssetSubtitleResponse";
import { useAssetsApi } from "../../../../../../common/api/master-api/assets/useAssetsApi";
import { OnPremAssetQueryKey } from "../../../common/OnPremAssetQueryKey";

export interface ValidateOnPremVideoAssetSubtitleParams {
    assetId?: string;
    subtitleLanguageId: string;
}

export interface UseEditOnPremAsset {
    validateOnPremVideoAssetSubtitle: SprintMutationResult<ValidateOnPremVideoAssetSubtitleParams, ValidationResult>;
    updateOnPremVideoAssetSubtitle: SprintMutationResult<
        UpdateOnPremVideoAssetSubtitleRequest,
        UpdateOnPremVideoAssetSubtitleResponse
    >;
}

export const useEditOnPremVideoAssetSubtitle = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditOnPremAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateOnPremVideoAssetSubtitle = useSprintMutation<
        ValidateOnPremVideoAssetSubtitleParams,
        ValidationResult
    >({
        mutationFn: async ({ assetId, subtitleLanguageId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate video asset subtitle.");
            }

            return assetsApi.validateOnPremVideoAssetUniqueness({ assetId, subtitleLanguageId });
        }
    });

    const updateOnPremVideoAssetSubtitle = useSprintMutation<
        UpdateOnPremVideoAssetSubtitleRequest,
        UpdateOnPremVideoAssetSubtitleResponse
    >({
        mutationFn: async ({ assetId, subtitleLanguageId }: UpdateOnPremVideoAssetSubtitleRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update video asset subtitle.");
            }

            return assetsApi.updateOnPremVideoAssetSubtitle({ assetId, subtitleLanguageId });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [OnPremAssetQueryKey.OnPremAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateOnPremVideoAssetSubtitle,
        updateOnPremVideoAssetSubtitle
    };
};
